import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const FIXED_CARDS = [
  { key: 'vision', label: 'Vision', cardType: 'vision' },
  { key: 'mission', label: 'Mission', cardType: 'mission' },
  { key: 'core-values', label: 'Core Values', cardType: 'core-values' }
];

export default function AboutUsVisionMissionValuesForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    intro: '',
    cards: []
  });

  const [pendingFields, setPendingFields] = useState({});
  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingCardIndex, setEditingCardIndex] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [modalCardData, setModalCardData] = useState(null);

  useEffect(() => {
    loadSection();
    loadMediaMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/aboutus/cms/media`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.ok && Array.isArray(result.data)) {
          const map = {};
          result.data.forEach(media => {
            const mediaId = media.id || media.media_id;
            const mediaUrl = media.url || media.file_path;
            if (mediaId && mediaUrl) {
              map[mediaId] = mediaUrl;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (err) {
      console.warn('Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[AboutUsVMVForm] Loading vision-mission-values section...');
      
      let valuesSection = null;
      
      // Try admin endpoint
      const adminResponse = await fetch(
        `${apiBase}/aboutus/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          valuesSection = result.data.find(s => s.section_type === 'about-us-values-shell');
        }
      }
      
      // Fallback to public endpoint
      if (!valuesSection) {
        const publicResponse = await fetch(
          `${apiBase}/aboutus/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            valuesSection = result.data.find(s => s.section_type === 'about-us-values-shell');
          }
        }
      }
      
      if (!valuesSection) {
        throw new Error('Vision-Mission-Values section not found');
      }

      setSectionId(valuesSection.id);
      console.log('[AboutUsVMVForm] Found section:', valuesSection.id);

      // Load pending changes
      let pendingFieldsMap = {};
      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${valuesSection.id}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        const pendingResult = await pendingResponse.json();
        if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
          const { pending_changes } = pendingResult.data;
          if (Array.isArray(pending_changes)) {
            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                // Handle card operations: cards.0.update, cards.1.update, etc
                const cardMatch = change.field_id.match(/^cards\.(\d+)\./);
                if (cardMatch) {
                  const cardIdx = parseInt(cardMatch[1]);
                  pendingFieldsMap[`card_${cardIdx}`] = true;
                  console.log('[AboutUsVMVForm] Card', cardIdx, 'has pending approval');
                } else {
                  // Regular field (kicker, title, intro)
                  pendingFieldsMap[change.field_id] = true;
                }
              }
            });
          }
        }
      }

      if (valuesSection.content_published) {
        let published = valuesSection.content_published;
        if (typeof published === 'string') {
          published = JSON.parse(published);
        }

        const publishedEn = published.en || {};
        const initialData = {
          kicker: publishedEn.kicker || '',
          title: publishedEn.title || '',
          intro: publishedEn.intro || '',
          cards: Array.isArray(publishedEn.cards) ? publishedEn.cards : []
        };

        setFormData(initialData);
        setOriginalData(initialData);
        setPendingFields(pendingFieldsMap);
        console.log('[AboutUsVMVForm] ✓ Section loaded with', initialData.cards.length, 'cards');
      }
    } catch (err) {
      console.error('[AboutUsVMVForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleCardChange = (cardIndex, field, value) => {
    console.log('[handleCardChange] Field:', field, 'Value:', value);
    // Always update modalCardData when editing in modal
    setModalCardData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      console.log('[handleCardChange] Updated modalCardData:', updated);
      return updated;
    });
  };

  const handleImageUpload = async (cardIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log('[AboutUsVMVForm] Image upload started:', { filename: file.name, cardIndex });

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(prev => ({ ...prev, [cardIndex]: true }));
    setImageProgress(prev => ({ ...prev, [cardIndex]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setImageProgress(prev => ({ ...prev, [cardIndex]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        console.log('[AboutUsVMVForm] Upload completed, status:', xhr.status);
        
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id;
              console.log('[AboutUsVMVForm] ✓ Image uploaded, media ID:', mediaId);
              if (mediaId) {
                handleCardChange(cardIndex, 'image', mediaId);
                toast.success('✓ Image uploaded (ID: ' + mediaId + ')');
              }
            }
          } catch (parseErr) {
            console.error('[AboutUsVMVForm] JSON parse error:', parseErr);
            toast.error('Invalid server response');
          }
        } else {
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(prev => ({ ...prev, [cardIndex]: false }));
      });

      xhr.addEventListener('error', () => {
        console.error('[AboutUsVMVForm] XHR error');
        toast.error('Upload failed - network error');
        setImageUploading(prev => ({ ...prev, [cardIndex]: false }));
      });

      xhr.open('POST', `${apiBase}/aboutus/cms/media/upload?section_type=about-us-values-shell`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      console.error('[AboutUsVMVForm] Upload error:', err);
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [cardIndex]: false }));
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' '),
            new_value: formData[fieldName],
            old_value: originalData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitCard = async (cardIndex, cardDataToSubmit = null) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // Use provided cardDataToSubmit (from modal) or fallback to formData
    const card = cardDataToSubmit || formData.cards[cardIndex];
    const cardKey = `card_${cardIndex}`;

    if (!card || !card.title || !card.description) {
      toast.warning('Please fill in title and description');
      return;
    }

    setSubmitting(prev => ({ ...prev, [cardKey]: true }));

    try {
      console.log('[AboutUsVMVForm] Submitting card for approval:', {
        cardIndex,
        cardLabel: FIXED_CARDS[cardIndex].label,
        sectionId,
        newCard: card
      });

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `cards.${cardIndex}.update`,
            field_name: `Update ${FIXED_CARDS[cardIndex].label}`,
            old_value: originalData.cards[cardIndex] || {},
            new_value: card,
            locale: 'en'
          })
        }
      );

      console.log('[AboutUsVMVForm] Response status:', response.status);
      const result = await response.json();
      console.log('[AboutUsVMVForm] Response result:', result);

      if (result.success || response.ok) {
        toast.success(`${FIXED_CARDS[cardIndex].label} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [cardKey]: true }));
        // Update both formData and originalData with the submitted card
        setFormData(prev => ({
          ...prev,
          cards: prev.cards.map((c, i) => i === cardIndex ? card : c)
        }));
        setOriginalData(prev => ({
          ...prev,
          cards: prev.cards.map((c, i) => i === cardIndex ? card : c)
        }));
      } else {
        if (response.status === 409) {
          toast.error('This card already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('[AboutUsVMVForm] Submit error:', err);
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [cardKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="hero-form-wrapper">
        <div className="loading">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading Vision, Mission & Core Values...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-form-wrapper">
      <ToastContainer />

      <div className="hero-form-header">
        <h1 className="hero-form-title">Vision, Mission & Core Values</h1>
        <p className="hero-form-description">Edit and submit each item individually for approval</p>
      </div>

      {/* Kicker */}
      <div className="form-section">
        <div className="field-header">
          <label>Section Kicker</label>
          {pendingFields.kicker && <span className="status-badge pending">Pending</span>}
        </div>
        <input
          type="text"
          maxLength="50"
          value={formData.kicker}
          onChange={(e) => handleFieldChange('kicker', e.target.value)}
          disabled={submitting.kicker || pendingFields.kicker}
          placeholder="e.g., Our Direction"
          className="text-input"
        />
        <button
          onClick={() => handleSubmitField('kicker')}
          disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
          className="submit-btn"
        >
          {submitting.kicker ? 'Submitting...' : 'Submit For Approval'}
        </button>
      </div>

      {/* Title */}
      <div className="form-section">
        <div className="field-header">
          <label>Section Title</label>
          {pendingFields.title && <span className="status-badge pending">Pending</span>}
        </div>
        <input
          type="text"
          maxLength="100"
          value={formData.title}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          disabled={submitting.title || pendingFields.title}
          placeholder="e.g., Vision, Mission, and Core Values"
          className="text-input"
        />
        <button
          onClick={() => handleSubmitField('title')}
          disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          className="submit-btn"
        >
          {submitting.title ? 'Submitting...' : 'Submit For Approval'}
        </button>
      </div>

      {/* Intro */}
      <div className="form-section">
        <div className="field-header">
          <label>Section Introduction</label>
          {pendingFields.intro && <span className="status-badge pending">Pending</span>}
        </div>
        <textarea
          maxLength="500"
          value={formData.intro}
          onChange={(e) => handleFieldChange('intro', e.target.value)}
          disabled={submitting.intro || pendingFields.intro}
          placeholder="Brief intro about these guiding principles..."
          className="textarea-input"
          rows="3"
        />
        <button
          onClick={() => handleSubmitField('intro')}
          disabled={submitting.intro || pendingFields.intro || formData.intro === originalData.intro}
          className="submit-btn"
        >
          {submitting.intro ? 'Submitting...' : 'Submit For Approval'}
        </button>
      </div>

      {/* Cards Section - Fixed 3 cards (Vision, Mission, Core Values) */}
      <div className="form-section">
        <h2 className="form-section-title">Vision, Mission & Core Values Cards</h2>

        {formData.cards.length === 0 ? (
          <div className="items-empty-state">
            <div className="empty-icon">📭</div>
            <h4>No cards available</h4>
            <p>Check the database configuration</p>
          </div>
        ) : (
          <div className="items-list">
            {FIXED_CARDS.map((fixedCard, idx) => {
              const card = formData.cards[idx];
              const cardKey = `card_${idx}`;
              const isPending = pendingFields[cardKey];
              
              if (!card) {
                return (
                  <div key={idx} className="items-list-item">
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                      Card data not found: {fixedCard.label}
                    </div>
                  </div>
                );
              }
              
              return (
                <div key={idx} className={`items-list-item ${isPending ? 'item-pending' : ''}`}>
                  <div className="items-list-content">
                    <h4 className="items-list-title">{fixedCard.label}</h4>
                    <p className="items-list-summary">{card.title || 'Click to edit'}</p>
                  </div>
                  
                  <div className="items-list-actions">
                    {isPending && (
                      <span className="items-list-status">Pending</span>
                    )}
                    <button
                      onClick={() => {
                        setEditingCardIndex(idx);
                        setModalCardData({ ...card });
                      }}
                      disabled={isPending}
                      className="items-list-btn items-list-btn-edit"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Card Modal */}
      {editingCardIndex !== null && modalCardData && (
        <div className="edit-modal-overlay" onClick={() => {
          setEditingCardIndex(null);
          setModalCardData(null);
        }}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-new">
              <h3>Edit {FIXED_CARDS[editingCardIndex].label}</h3>
              <button 
                className="modal-close-btn" 
                onClick={() => {
                  setEditingCardIndex(null);
                  setModalCardData(null);
                }}
              >×</button>
            </div>

            <div className="modal-body">
              {/* Title Field */}
              <div className="modal-field-group">
                <label>Title</label>
                <input
                  type="text"
                  value={modalCardData?.title || ''}
                  onChange={(e) => {
                    console.log('[Modal Input] onChange fired, target value:', e.target.value);
                    handleCardChange(editingCardIndex, 'title', e.target.value);
                  }}
                  placeholder={`${FIXED_CARDS[editingCardIndex].label} title...`}
                  className="modal-input"
                  maxLength="300"
                  autoFocus
                />
              </div>

              {/* Description Field */}
              <div className="modal-field-group">
                <label>Description</label>
                <textarea
                  value={modalCardData?.description || ''}
                  onChange={(e) => handleCardChange(editingCardIndex, 'description', e.target.value)}
                  placeholder={`${FIXED_CARDS[editingCardIndex].label} description...`}
                  className="modal-textarea"
                  maxLength="500"
                  rows="5"
                />
              </div>

              {/* Image Section */}
              <div className="modal-field-group">
                <label>Card Image</label>
                {modalCardData?.image && (
                  <div className="modal-image-section">
                    <div className="modal-image-preview">
                      {mediaMap[modalCardData.image] ? (
                        <img 
                          src={mediaMap[modalCardData.image]} 
                          alt={FIXED_CARDS[editingCardIndex].label}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="modal-image-placeholder" style={{display: mediaMap[modalCardData.image] ? 'none' : 'flex'}}>
                        <span>ID: {modalCardData.image} (URL not found)</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="modal-upload-area">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(editingCardIndex, e)}
                    disabled={imageUploading[editingCardIndex] || pendingFields[`card_${editingCardIndex}`]}
                    className="modal-file-input"
                    id={`card-image-${editingCardIndex}`}
                  />
                  <label
                    htmlFor={`card-image-${editingCardIndex}`}
                    className="modal-upload-label"
                  >
                    <span className="upload-icon-new">📤</span>
                    <span>
                      {imageUploading[editingCardIndex] ? `Uploading ${imageProgress[editingCardIndex]}%...` : 'Click to upload image'}
                    </span>
                  </label>
                </div>
                <span className="hint">Max 5MB • Recommended: 400×300px</span>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="modal-btn-cancel" 
                onClick={() => {
                  setEditingCardIndex(null);
                  setModalCardData(null);
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  // Submit directly with modalCardData
                  handleSubmitCard(editingCardIndex, modalCardData);
                  setEditingCardIndex(null);
                  setModalCardData(null);
                }}
                disabled={submitting[`card_${editingCardIndex}`] || pendingFields[`card_${editingCardIndex}`] || !modalCardData?.title || !modalCardData?.description}
                className="modal-btn-submit"
              >
                {submitting[`card_${editingCardIndex}`] ? 'Submitting...' : 'Save & Submit For Approval'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
